// Fires `page_view` on every route change. Mounted inside the rendered app
// shell (`Agency`) so it's always under `<BrowserRouter>` — `useLocation`
// would throw at the App-level otherwise.
//
// It lived in `TenantLayout` until 2026-08-24, which was never mounted in the
// route tree, so this event silently never fired. Mount it in the component
// that actually wraps `privateRoutes`, and nowhere else.
//
// Query string is passed through a redaction pass: any param key matching
// the blocklist regex (token, email, phone, ssn, dob, zip, password) is
// replaced with `[redacted]`. The pathname itself is left intact — by
// convention, tenant-admin routes don't embed PII (only IDs / slugs).
//
// SEE: docs/tenant-admin-vexo-analytics-plan.md §3 + §9 edge case #14, #22.

import { useReactiveVar } from '@apollo/client';
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { currentAdminVar } from '@/util/apollo/cache';

import { track } from './events';

const BLOCKED_PARAM_KEY = /(token|email|phone|ssn|dob|zip|password)/i;

function redactQueryString(search: string): string {
  if (!search) return '';
  const params = new URLSearchParams(
    search.startsWith('?') ? search.slice(1) : search,
  );
  let mutated = false;
  for (const k of Array.from(params.keys())) {
    if (BLOCKED_PARAM_KEY.test(k)) {
      params.set(k, '[redacted]');
      mutated = true;
    }
  }
  if (!mutated) return search;
  const out = params.toString();
  return out ? `?${out}` : '';
}

export function usePageViewTracking(): void {
  const { pathname, search } = useLocation();
  // `Agency` renders before `Authenticated` has resolved the actor query, so
  // on a cold load this is null for the first paint or two.
  const admin = useReactiveVar(currentAdminVar);
  const lastSent = useRef<string | null>(null);

  // Edge case #14: hash-only changes don't fire because `hash` isn't in
  // the deps. Edge case #9: if SDK isn't loaded yet, track() no-ops; the
  // first route is silently lost (acceptable; one event per session).
  useEffect(() => {
    // Hold the event until identity exists. `baseProps()` reads
    // `currentAdminVar()` at fire time, so emitting before it hydrates sends
    // role / actor_type / account_id / client_id as null. graphy fills
    // user_id + admin_id from the JWT regardless, which makes such an event
    // look attributed while every dimension a breakdown groups on is empty.
    //
    // Prod evidence (2026-08-26, first 44h of the release): 949 of 1,018
    // sessions — 93.2% — lost their ENTRY page this way, worst on `/`,
    // `/inbox` and `/job-monitor`. Deferring costs nothing: the effect
    // re-runs the moment the var is written, and reports the same location.
    //
    // If the actor query never resolves we emit nothing rather than an
    // unattributable row. That is the intended trade — a page_view that
    // can't be grouped by role or client is not worth the row.
    if (!admin) return;

    // `admin` is in the deps so hydration re-runs this, but the var is also
    // rewritten on agency switch. Dedupe on the location so that only a real
    // navigation emits, never a re-identify on the page we already reported.
    const key = `${pathname}${search}`;
    if (lastSent.current === key) return;
    lastSent.current = key;

    track('page_view', {
      path: pathname,
      search: redactQueryString(search),
    });
  }, [pathname, search, admin]);
}
